import React, { useEffect, useState } from "react";
import { Camera, Upload, Loader2, MapPin, X, CheckCircle2, AlertTriangle, CloudUpload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, queueMediaUpload } from "@/lib/api";
import { downscaleImage } from "@/lib/imageUtils";
import { PHASES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Progressive longest-edge sizes: shrink further whenever the proxy rejects a photo (413).
const SIZE_STEPS = [2048, 1600, 1280, 1024, 800];

function getLocation() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve(null), { timeout: 5000, maximumAge: 60000 });
  });
}

let itemSeq = 0;

export default function MediaUploader({ links, onUploaded, allowPhase = false, endpoint = "/media", extraFields = {}, compact = false, documentsOnly = false, defaultPhase = "" }) {
  const [items, setItems] = useState([]); // {uid, file, captureType, preview, status}
  const [desc, setDesc] = useState("");
  const [stamp, setStamp] = useState(true);
  const [phase, setPhase] = useState(defaultPhase);
  useEffect(() => { setPhase(defaultPhase); }, [defaultPhase]);
  const [busy, setBusy] = useState(false);

  const addFiles = (fileList, captureType) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    setItems((prev) => [
      ...prev,
      ...arr.map((file) => ({
        uid: `m${++itemSeq}`, file, captureType,
        preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
        status: "staged",
      })),
    ]);
  };

  const removeItem = (uid) => setItems((prev) => {
    const it = prev.find((x) => x.uid === uid);
    if (it?.preview) URL.revokeObjectURL(it.preview);
    return prev.filter((x) => x.uid !== uid);
  });

  const clearAll = () => setItems((prev) => { prev.forEach((x) => x.preview && URL.revokeObjectURL(x.preview)); return []; });

  const buildFd = (payload, item, loc) => {
    const fd = new FormData();
    fd.append("file", payload, payload.name || item.file.name || "foto.jpg");
    Object.entries({ ...links, ...extraFields }).forEach(([k, v]) => v && fd.append(k, v));
    fd.append("description", desc);
    fd.append("capture_type", item.captureType);
    fd.append("timestamp", stamp ? "true" : "false");
    if (phase) fd.append("phase", phase);
    if (loc && Number.isFinite(loc.lat) && Number.isFinite(loc.lng)) { fd.append("lat", loc.lat); fd.append("lng", loc.lng); }
    return fd;
  };

  // Returns "done" (uploaded now), "queued" (will upload in background), or "error".
  const uploadOne = async (item, loc) => {
    const isImage = item.file.type.startsWith("image/");
    if (!isImage) {
      const fd = buildFd(item.file, item, loc);
      try { const res = await api.post(endpoint, fd); return res?.__queued ? "queued" : "done"; }
      catch (e) {
        const s = e.response?.status;
        if (!s || s >= 500) { try { await queueMediaUpload(endpoint, buildFd(item.file, item, loc)); return "queued"; } catch (_) { return "error"; } }
        toast.error(formatApiError(e.response?.data?.detail));
        return "error";
      }
    }
    for (const dim of SIZE_STEPS) {
      let payload = item.file;
      try { payload = await downscaleImage(item.file, { maxDim: dim, quality: 0.82 }); } catch (_) { payload = item.file; }
      const fd = buildFd(payload, item, loc);
      try {
        const res = await api.post(endpoint, fd);
        return res?.__queued ? "queued" : "done";
      } catch (e) {
        const s = e.response?.status;
        if (s === 413) continue;                 // too large → shrink more and retry
        if (!s || s >= 500) {                     // transient server/network → durable background retry
          try { await queueMediaUpload(endpoint, fd); return "queued"; } catch (_) { return "error"; }
        }
        continue;                                 // unexpected 4xx → try a smaller version, else queue below
      }
    }
    // Every size still failed → queue the smallest version so it uploads later no matter what.
    try {
      let payload = item.file;
      try { payload = await downscaleImage(item.file, { maxDim: 800, quality: 0.65 }); } catch (_) {}
      await queueMediaUpload(endpoint, buildFd(payload, item, loc));
      return "queued";
    } catch (_) { return "error"; }
  };

  const uploadAll = async () => {
    const pendingItems = items.filter((x) => x.status !== "done" && x.status !== "queued");
    if (!pendingItems.length) return;
    setBusy(true);
    const loc = await getLocation();
    const results = [];
    for (const item of pendingItems) {
      setItems((prev) => prev.map((x) => (x.uid === item.uid ? { ...x, status: "uploading" } : x)));
      const r = await uploadOne(item, loc);
      results.push([item.uid, r]);
      setItems((prev) => prev.map((x) => (x.uid === item.uid ? { ...x, status: r } : x)));
    }
    setBusy(false);
    const ok = results.filter(([, r]) => r === "done").length;
    const queued = results.filter(([, r]) => r === "queued").length;
    const errored = results.filter(([, r]) => r === "error").length;
    if (ok) toast.success(`${ok} Foto${ok > 1 ? "s" : ""} hochgeladen`);
    if (queued) toast.info(`${queued} Foto${queued > 1 ? "s" : ""} werden hochgeladen, sobald Internet verfügbar ist`);
    if (errored) toast.error(`${errored} Foto${errored > 1 ? "s" : ""} konnten nicht verarbeitet werden`);
    // Drop everything that is done or safely queued; keep only hard errors for a manual retry.
    setItems((prev) => prev.filter((x) => {
      const r = results.find(([u]) => u === x.uid)?.[1];
      if (r === "done" || r === "queued") { if (x.preview) URL.revokeObjectURL(x.preview); return false; }
      return true;
    }));
    if (ok || queued) { setDesc(""); setPhase(""); onUploaded && onUploaded(); }
  };

  const box = compact ? "p-2 flex-row gap-2 text-xs" : "p-4 flex-col gap-1 text-sm";
  const pendingCount = items.filter((x) => x.status !== "done" && x.status !== "queued").length;

  const pickTiles = (
    <div className="grid grid-cols-2 gap-2" data-testid="media-uploader">
      {!documentsOnly && (
        <label className="cursor-pointer">
          <input type="file" accept="image/*" capture="environment" multiple className="hidden" data-testid="media-camera-input" onChange={(e) => { addFiles(e.target.files, "kamera"); e.target.value = ""; }} />
          <div className={`border-2 border-dashed border-slate-200 rounded-xl flex items-center justify-center hover:border-primary/50 hover:bg-slate-50 ${box}`}>
            <Camera className="h-5 w-5 text-primary" /><span className="font-medium">Foto aufnehmen</span>
          </div>
        </label>
      )}
      <label className={`cursor-pointer ${documentsOnly ? "col-span-2" : ""}`}>
        <input type="file" multiple className="hidden" accept={documentsOnly ? ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.dwg,.dxf" : undefined} data-testid="media-file-input" onChange={(e) => { addFiles(e.target.files, documentsOnly ? "upload" : "upload"); e.target.value = ""; }} />
        <div className={`border-2 border-dashed border-slate-200 rounded-xl flex items-center justify-center hover:border-primary/50 hover:bg-slate-50 ${box}`}>
          <Upload className="h-5 w-5 text-sky-600" /><span className="font-medium">{documentsOnly ? "Dokument hochladen" : "Fotos auswählen"}</span>
        </div>
      </label>
    </div>
  );

  if (items.length === 0) return pickTiles;

  return (
    <div className="border border-primary/40 bg-amber-50/40 rounded-xl p-3 space-y-3" data-testid="media-staging-panel">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-600">{items.length} Foto{items.length > 1 ? "s" : ""} ausgewählt</span>
        <button onClick={clearAll} disabled={busy} className="text-xs text-slate-400 hover:text-red-500" data-testid="media-clear-btn">Alle verwerfen</button>
      </div>

      <div className="grid grid-cols-4 gap-2" data-testid="media-staged-grid">
        {items.map((it) => (
          <div key={it.uid} className="relative" data-testid={`media-staged-item-${it.uid}`}>
            {it.preview
              ? <img src={it.preview} alt="" className="h-16 w-full rounded-lg object-cover border" />
              : <div className="h-16 w-full rounded-lg bg-slate-100 flex items-center justify-center text-[10px] text-slate-500 px-1 text-center border">{(it.file.name.split(".").pop() || "DAT").toUpperCase()}</div>}
            {it.status === "uploading" && <div className="absolute inset-0 rounded-lg bg-black/40 flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-white" /></div>}
            {it.status === "done" && <div className="absolute inset-0 rounded-lg bg-emerald-600/40 flex items-center justify-center"><CheckCircle2 className="h-4 w-4 text-white" /></div>}
            {it.status === "queued" && <div className="absolute inset-0 rounded-lg bg-sky-600/40 flex items-center justify-center"><CloudUpload className="h-4 w-4 text-white" /></div>}
            {it.status === "error" && <div className="absolute inset-0 rounded-lg bg-red-600/30 flex items-center justify-center"><AlertTriangle className="h-4 w-4 text-white" /></div>}
            {!busy && it.status !== "done" && it.status !== "queued" && (
              <button onClick={() => removeItem(it.uid)} className="absolute -top-1.5 -right-1.5 bg-white rounded-full p-0.5 shadow border text-slate-400 hover:text-red-500" data-testid={`media-remove-${it.uid}`}><X className="h-3 w-3" /></button>
            )}
          </div>
        ))}
      </div>

      <Input placeholder="Beschreibung (für alle Fotos)…" value={desc} onChange={(e) => setDesc(e.target.value)} className="h-8 text-sm" data-testid="media-description-input" />
      <div className="flex items-center gap-3 flex-wrap text-xs">
        <label className="flex items-center gap-1.5"><Switch checked={stamp} onCheckedChange={setStamp} data-testid="media-timestamp-switch" /><span>Zeitstempel</span></label>
        {allowPhase && (
          <Select value={phase || "none"} onValueChange={(v) => setPhase(v === "none" ? "" : v)}>
            <SelectTrigger className="h-7 w-40 text-xs" data-testid="media-phase-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PHASES.map((p) => <SelectItem key={p.value || "none"} value={p.value || "none"}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <span className="flex items-center gap-1 text-slate-400"><MapPin className="h-3 w-3" />Standort beim Hochladen</span>
      </div>

      {pickTiles}

      <Button onClick={uploadAll} disabled={busy || pendingCount === 0} className="w-full h-9" data-testid="media-upload-all-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Alle hochladen (${pendingCount})`}
      </Button>
    </div>
  );
}
